import { callApi } from './api';
import AstroTipContent from '@/models/astro-tip-content';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const astroTipsApiUrl = import.meta.env.VITE_API_URL_V1 + '/astro-tip-contents';

export const getAstroTips = async () => {
    return callApi<AstroTipContent[]>({ url: astroTipsApiUrl, method: 'GET' });
};

export const useAstroTips = () => {
    return useQuery({ queryKey: ['astro-tips'], queryFn: getAstroTips });
};

export const getAstroTip = async (id: string) => {
    return callApi<AstroTipContent>({ url: `${astroTipsApiUrl}/${id}`, method: 'GET' });
};

export const useAstroTip = (id: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['astro-tip', id],
        queryFn: () => getAstroTip(id),
        enabled: !!id && isOpen,
    });
};

/**
 * Only editable fields go over the wire (the API's validate() rejects unmatched
 * body keys). Optional override maps (wordLine/elementBody) and `kicker` are
 * omitted when empty; `headlines`, `dos`, `donts` are always sent (possibly
 * empty) since a pool may be partial.
 */
export type AstroTipMutationPayload = {
    name: string;
    active?: boolean;
    kicker?: string;
    headlines: { [dayWord: string]: string[] };
    wordLine?: { [dayWord: string]: string };
    elementBody?: { [element: string]: string };
    dos: string[];
    donts: string[];
};

export const createAstroTip = async (payload: AstroTipMutationPayload) => {
    return callApi<AstroTipContent>({ url: astroTipsApiUrl, method: 'POST', data: payload });
};

export const useCreateAstroTip = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createAstroTip,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['astro-tips'] });
        },
    });
};

export const updateAstroTip = async ({ id, ...payload }: AstroTipMutationPayload & { id: string }) => {
    return callApi<AstroTipContent>({ url: `${astroTipsApiUrl}/${id}`, method: 'PUT', data: payload });
};

export const useUpdateAstroTip = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateAstroTip,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['astro-tips'] });
            queryClient.invalidateQueries({ queryKey: ['astro-tip', variables.id] });
        },
    });
};

export const deleteAstroTip = async ({ id }: { id: string }) => {
    return callApi({ url: `${astroTipsApiUrl}/${id}`, method: 'DELETE' });
};

export const useDeleteAstroTip = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteAstroTip,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['astro-tips'] });
        },
    });
};
